import React, { useState, useEffect } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { CheckCircle, XCircle, MapPin } from 'lucide-react';
import AssignmentCanvas from './AssignmentCanvas';

export default function RequestsManager({ exhibition }) {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [exhibition]);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/requests?exhibition_id=${exhibition.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data);
    } catch (error) {
      toast.error('Talepler yüklenemedi');
    }
  };

  const updateRequestStatus = async (requestId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/requests/${requestId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Durum güncellendi');
      fetchRequests();
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const handleAssignStand = (request) => {
    setSelectedRequest(request);
    setShowAssignDialog(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'default',
      approved: 'success',
      rejected: 'destructive'
    };
    const labels = {
      pending: 'Beklemede',
      approved: 'Onaylandı',
      rejected: 'Reddedildi'
    };
    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>Stant Talepleri</CardTitle>
        <CardDescription>{exhibition.name} için gelen talepler</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {requests.map(request => (
            <Card key={request.id} data-testid={`request-card-${request.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{request.company_name}</CardTitle>
                    <CardDescription>Alan: {request.area_required} m²</CardDescription>
                  </div>
                  {getStatusBadge(request.status)}
                </div>
              </CardHeader>
              <CardContent>
                {request.notes && (
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>Notlar:</strong> {request.notes}
                  </p>
                )}
                {request.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAssignStand(request)}
                      size="sm"
                      data-testid={`assign-btn-${request.id}`}
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Yer Ata
                    </Button>
                    <Button
                      onClick={() => updateRequestStatus(request.id, 'rejected')}
                      variant="destructive"
                      size="sm"
                      data-testid={`reject-btn-${request.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reddet
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {requests.length === 0 && (
            <p className="text-center text-gray-500 py-8">Henüz talep bulunmuyor</p>
          )}
        </div>
      </CardContent>

      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Stant Ata - {selectedRequest?.company_name}</DialogTitle>
            <DialogDescription>
              Gereken alan: {selectedRequest?.area_required} m²
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <AssignmentCanvas
              exhibition={exhibition}
              request={selectedRequest}
              onAssigned={() => {
                setShowAssignDialog(false);
                fetchRequests();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}